import { useEffect, useMemo, useReducer, useRef } from 'react';
import { resourceRoutes } from '@/route-paths';

export type ColorCountStatus = 'idle' | 'loading' | 'success' | 'error';
export type ColorCountMap = {
    status: ColorCountStatus;
    [key: string]: number | null | undefined | ColorCountStatus;
};

type ColorCountState = {
    data: Record<string, number | null | undefined>;
    status: ColorCountStatus;
};

type ColorCountAction =
    | { type: 'reset-empty' }
    | { type: 'start'; productIds: string[] }
    | { type: 'success'; payload: Record<string, number | null | undefined> }
    | { type: 'error' };

const createInitialData = (productIds: string[]) =>
    Object.fromEntries(productIds.map((productId) => [productId, null])) as Record<string, number | null | undefined>;

const createInitialState = (productIds: string[]): ColorCountState => ({
    data: createInitialData(productIds),
    status: 'idle',
});

function colorCountReducer(state: ColorCountState, action: ColorCountAction): ColorCountState {
    switch (action.type) {
        case 'reset-empty': {
            return { data: {}, status: 'idle' };
        }
        case 'start': {
            const nextData = Object.fromEntries(
                action.productIds.map((productId) => [productId, state.data[productId] ?? null])
            ) as Record<string, number | null | undefined>;

            return { data: nextData, status: 'loading' };
        }
        case 'success': {
            return { data: { ...state.data, ...action.payload }, status: 'success' };
        }
        case 'error': {
            return { ...state, status: 'error' };
        }
        default: {
            return state;
        }
    }
}

/**
 * Fetches product color counts for the current batch of product ids.
 *
 * Design notes:
 * - ignore stale responses from older requests
 * - keep status + data in one object for easier downstream use
 */
export function useProductColorCounts(productIds: string[]): ColorCountMap {
    const stableKey = useMemo(() => JSON.stringify(productIds), [productIds]);
    const requestIdRef = useRef(0);
    const lastRequestKeyRef = useRef<string | null>(null);
    const [state, dispatch] = useReducer(colorCountReducer, createInitialState(productIds));

    useEffect(() => {
        if (productIds.length === 0) {
            lastRequestKeyRef.current = null;
            requestIdRef.current += 1;
            dispatch({ type: 'reset-empty' });
            return;
        }

        if (lastRequestKeyRef.current === stableKey) {
            return;
        }

        lastRequestKeyRef.current = stableKey;

        const requestId = ++requestIdRef.current;
        dispatch({ type: 'start', productIds });

        const formData = new FormData();
        for (const productId of productIds) {
            formData.append('productIds', productId);
        }

        void fetch(resourceRoutes.productColorCounts, {
            method: 'POST',
            body: formData,
        })
            .then(async (response) => {
                let responseObj;
                try {
                    responseObj = await response.json();
                } catch {
                    dispatch({ type: 'error' });
                    return;
                }

                if (requestId !== requestIdRef.current) {
                    return;
                }

                if (!response.ok || responseObj.success === false) {
                    dispatch({ type: 'error' });
                    return;
                }

                if (responseObj.data) {
                    dispatch({ type: 'success', payload: responseObj.data });
                    return;
                }

                dispatch({ type: 'error' });
            })
            .catch(() => {
                if (requestId !== requestIdRef.current) {
                    return;
                }
                dispatch({ type: 'error' });
            });
    }, [productIds, stableKey]);

    return useMemo<ColorCountMap>(() => ({ ...state.data, status: state.status }), [state.data, state.status]);
}
